import { GridColDef, GridCellParams } from '@mui/x-data-grid';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { maskPersonalIdentityNumber } from 'src/shared/helpers/personalIdentityNumberHelper';
import { Box, Link } from '@mui/material';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { useGlobals } from 'src/shared/contexts/appContext';
import { DonorAutogiroSubscription, getDonorAutogiroSubscriptions } from 'src/shared/services/donorService';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { DonorAutogiroFilter } from '../models/donorAutogiroFilter';
import { MandateStatusWarning, mandateBlocksPayment } from './mandateStatusWarning';

type DonorAutogiroDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: DonorAutogiroFilter;
  searchFilterCallback: (filter: DonorAutogiroFilter) => void;
};

/**
 * En rad per aktiv prenumeration, inte per givare. Periodicitet, belopp och nästa dragning hör till
 * prenumerationen, och en givare med 260 kr i månaden och 10 kr i kvartalet gick inte att visa på en
 * rad utan att ljuga. Uppdelningen görs i API:et så att paginering och antal räknas på raderna.
 */
export const DonorAutogiroDataGrid = (props: DonorAutogiroDataGridProps) => {
  const { loadingCallback, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['invoice']);
  const { dateStamp } = useGlobals();
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const isMounted = useIsMounted();

  const [rows, setRows] = useState<DonorAutogiroSubscription[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getDonorAutogiroSubscriptions(
        searchFilter.pageIndex,
        searchFilter.pageSize,
        searchFilter.sortCol,
        searchFilter.sortDir,
        searchFilter.filter,
        searchFilter.onlyBlocked,
      );

      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          ConsoleHelper.log(dateStamp);
          setRows(result.data.results.map((item) => ({ ...item, id: `${item.donor_id}-${item.subscription_id ?? 'none'}` })));
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, searchFilter, dateStamp]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  // Nästa dragning räknas fram på samma sätt som i månadsgivarlistan under Gåvor, från senaste
  // skapade faktura plus prenumerationens intervall.
  const nextDraw = (row: DonorAutogiroSubscription) => {
    if (!row.lastInvoiceCreated || !row.monthlyFrequency) return undefined;

    return dayjs(row.lastInvoiceCreated).add(row.monthlyFrequency, 'month');
  };

  const translateFrequency = (frequency: number) => {
    if (frequency === 1) return t('invoice:autogiro.donorFrequencyMonth');
    if (frequency === 3) return t('invoice:autogiro.donorFrequencyQuarter');
    if (frequency === 12) return t('invoice:autogiro.donorFrequencyYear');
    return '';
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('invoice:autogiro.tableHeader.donor'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <Link href={`${RoutesEnum.DonorsDetail}/${params.row.donor_id}`}>{params.row.name}</Link>;
      },
    },
    {
      field: 'personalIdentityNumber',
      headerName: t('invoice:autogiro.labels.personalIdentityNumber'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{maskPersonalIdentityNumber(params.row.personalIdentityNumber)}</>;
      },
    },
    {
      field: 'monthlyFrequency',
      headerName: t('invoice:autogiro.tableHeader.periodicity'),
      sortable: false,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        if (!params.row.monthlyFrequency) return <>{t('invoice:autogiro.donorNoActiveSubscription')}</>;
        return <>{translateFrequency(params.row.monthlyFrequency)}</>;
      },
    },
    {
      field: 'createdAt',
      headerName: t('invoice:autogiro.tableHeader.createdAt'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDate(params.row.createdAt)}</>;
      },
    },
    {
      field: 'nextDraw',
      headerName: t('invoice:autogiro.tableHeader.nextInvoiceAfter'),
      sortable: false,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const date = nextDraw(params.row);
        const blocked = mandateBlocksPayment(true, params.row.mandateStatus);

        return (
          <Box display='flex' alignItems='center' gap={0.5}>
            <span style={blocked ? { textDecoration: 'line-through' } : undefined}>
              {date ? date.format('YYYY-MM-DD') : t('invoice:autogiro.donorNoNextDraw')}
            </span>
            <MandateStatusWarning hasMandate status={params.row.mandateStatus} canceledTextKey='invoice:autogiro.donorMandateCanceledWarning' />
          </Box>
        );
      },
    },
    {
      field: 'amount',
      headerAlign: 'left',
      align: 'left',
      headerName: t('invoice:autogiro.tableHeader.amount'),
      sortable: false,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        if (!params.row.amount) return <></>;
        return <AmountWithCurrency amount={params.row.amount} />;
      },
    },
  ];

  return (
    <Box ref={scrollRef}>
      <DataGrid<DonorAutogiroSubscription>
        columns={columns}
        rows={rows}
        autoHeight
        dataModel={data}
        checkboxSelection={false}
        disableSelectionOnClick
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={[{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]}
        onSortModelChange={(newSortModel) => {
          if (!newSortModel.length) return;

          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel[0].field,
            sortDir: newSortModel[0].sort ?? 'desc',
          });
        }}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
